/** Edit a sprint's name, goal, and dates.
 *
 * Sprints are named and dated automatically at creation, which is right most of the time
 * and wrong often enough to matter — a holiday fortnight, a sprint that started late.
 * Status is deliberately absent: it moves only through Start and Complete, which carry
 * their own rules.
 */

import { useState } from 'react'

import { Button, DateInput, ErrorState, Field, inputClass } from '@/components/ui'
import { useUpdateSprint, type Sprint } from '@/features/workbench/useWork'

export function EditSprintDialog({ sprint, onClose }: { sprint: Sprint; onClose: () => void }) {
  const update = useUpdateSprint()
  const [form, setForm] = useState({
    name: sprint.name,
    goal: sprint.goal,
    start_date: sprint.startDate,
    end_date: sprint.endDate,
  })

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }))

  const datesInverted = Boolean(
    form.start_date && form.end_date && form.end_date < form.start_date,
  )

  const submit = () => {
    update.mutate(
      {
        id: sprint.id,
        name: form.name,
        goal: form.goal,
        start_date: form.start_date,
        end_date: form.end_date,
        // Sent so a concurrent edit conflicts instead of silently overwriting.
        row_version: sprint.rowVersion,
      },
      { onSuccess: onClose },
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${sprint.name}`}
      >
        <h2 className="mb-4 font-heading text-2xl text-fg">Edit sprint</h2>

        {update.error ? (
          <div className="mb-4">
            <ErrorState error={update.error} />
          </div>
        ) : null}

        <div className="space-y-3">
          <Field label="Name">
            <input className={inputClass} value={form.name} onChange={set('name')} autoFocus />
          </Field>

          <Field label="Goal" hint="What the fortnight is for.">
            <textarea className={inputClass} rows={2} value={form.goal} onChange={set('goal')} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <DateInput
                value={form.start_date}
                onChange={(value) => set('start_date')({ target: { value } })}
              />
            </Field>
            <Field label="End date">
              <DateInput
                value={form.end_date}
                onChange={(value) => set('end_date')({ target: { value } })}
              />
            </Field>
          </div>

          {datesInverted ? (
            <p className="text-xs" style={{ color: 'var(--status-red)' }}>
              A sprint cannot end before it starts.
            </p>
          ) : null}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!form.name.trim() || datesInverted || update.isPending}
            onClick={submit}
          >
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}
